import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useStoreActions } from 'easy-peasy';
import { useNavigate } from 'react-router-dom';
import { dateFormat } from '../../tools/manager';

// import icons
import AttentionIcon from '../../../assets/images/svg/attention.svg';
import Modal from '../Modal_2';
import Search from '../../../assets/images/svg/search.svg';
import Table from '../Table';

const ListClassifyInterventionCoupon = () => {
  const navigate = useNavigate();

  // --- State ---
  const [showModalMove, setShowModalMove] = useState(false);
  const [interventionCoupons, setInterventionCoupons] = useState([]);
  const [textSearch, setTextSearch] = useState('');
  const [idCoupon, setIdCoupon] = useState(null);
  const [namesCompagny, setNamesCompagny] = useState([]);
  const [valuesWorksites, setValuesWorksites] = useState([]);
  const [selectWorksite, setSelectWorksite] = useState('');

  // Pagination
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // --- Store actions ---
  const {
    getListInterventionCoupon: fetchInterventionCoupons,
    moveInterventionCouponToWorksite,
  } = useStoreActions((a) => a.middleware.OrdersMiddleware);

  const {
    getListWorksitesClassify,
    getListCompanyClassify,
  } = useStoreActions((a) => a.middleware.OrdersMiddleware);

  const getUser = useStoreActions((a) => a.middleware.AdminMiddleware.getAdmin);

  // --- Utils ---
  const formatDate = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1)
      .padStart(2, '0')}.${d.getFullYear()}`;
  };

  // --- Fetch coupons (first page) ---
  const loadInterventionCoupons = useCallback(async () => {
    try {
      const res = await fetchInterventionCoupons({ limit: 25, nextToken: null });
      const items = res?.items || [];
      const token = res?.nextToken || null;

      const enriched = await Promise.all(
        items.map(async (el) => {
          if (el?.name === 'intervention urgent' && el?.interventionCouponUserId) {
            const user = await getUser(el.interventionCouponUserId);
            return {
              ...el,
              username: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
            };
          }
          return { ...el, username: el?.name || '' };
        }),
      );

      setInterventionCoupons(enriched);
      setNextToken(token);
    } catch {
      toast.error("Erreur lors de la récupération des bons d'intervention");
    }
  }, [fetchInterventionCoupons, getUser]);

  // --- Load more (pagination) ---
  const loadMore = async () => {
    if (!nextToken) return;

    setLoadingMore(true);
    try {
      const res = await fetchInterventionCoupons({ limit: 25, nextToken });
      const newItems = res?.items || [];
      const newToken = res?.nextToken || null;

      const enriched = await Promise.all(
        newItems.map(async (el) => {
          if (el?.name === 'intervention urgent' && el?.interventionCouponUserId) {
            const user = await getUser(el.interventionCouponUserId);
            return {
              ...el,
              username: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
            };
          }
          return { ...el, username: el?.name || '' };
        }),
      );

      const merged = [...interventionCoupons, ...enriched];
      setInterventionCoupons(merged);
      setNextToken(newToken);
    } catch {
      toast.error('Erreur lors du chargement supplémentaire');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadCompanies = useCallback(async () => {
    try {
      const res = await getListCompanyClassify();
      setNamesCompagny(res?.[0]?.companies?.items || []);
    } catch {
      toast.error('Erreur lors de la récupération des clients');
    }
  }, [getListCompanyClassify]);

  const loadWorksites = useCallback(async () => {
    try {
      await getListWorksitesClassify(); // gardé si besoin plus tard
    } catch {
      toast.error('Erreur lors de la récupération des chantiers');
    }
  }, [getListWorksitesClassify]);

  useEffect(() => {
    loadInterventionCoupons();
    loadWorksites();
    loadCompanies();
  }, [loadInterventionCoupons, loadWorksites, loadCompanies]);

  // --- Derived data ---
  const filteredCoupons = useMemo(() => {
    const search = textSearch.trim().toLowerCase();
    if (!search) return interventionCoupons;

    return interventionCoupons.filter((el) => {
      const d = dateFormat(el?.createdAt);
      return (
        el?.nameClient?.toLowerCase().includes(search) ||
        el?.nameWorksite?.toLowerCase().includes(search) ||
        el?.numero?.toString().includes(search) ||
        el?.username?.toLowerCase().includes(search) ||
        d.day.includes(search) ||
        d.month.includes(search) ||
        d.year.includes(search)
      );
    });
  }, [interventionCoupons, textSearch]);

  const sortedCoupons = useMemo(
    () => [...filteredCoupons].sort((a, b) => Number(b.numero) - Number(a.numero)),
    [filteredCoupons],
  );

  // --- Handlers ---
  const handleMove = (id) => {
    setIdCoupon(id);
    setShowModalMove(true);
  };

  const handleSelectCompany = (e) => {
    const company = JSON.parse(e.target.value);
    setValuesWorksites(company?.worksites?.items || []);
    setSelectWorksite('');
  };

  const handleSelectWorksite = (e) =>
    setSelectWorksite(e.target.value);

  const confirmMoveCoupon = async () => {
    try {
      await moveInterventionCouponToWorksite({ id: idCoupon, idWorksite: selectWorksite });
      toast.success('Bon déplacé avec succès ✅');
      setShowModalMove(false);
      loadInterventionCoupons();
    } catch {
      toast.error("Erreur lors du déplacement du bon d'intervention");
    }
  };

  // --- Render ---
  return (
    <section className="md:pt-8 px-4 md:px-9">
      <div className="h-[500px]">
        {/* Search bar */}
        <div className="relative flex mt-6 border border-gray-admin rounded-xl">
          <input
            type="text"
            placeholder="Recherche bons"
            className="w-full py-3 pl-10 rounded-xl placeholder:text-blue-admin inputFocus"
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
          />
          <img src={Search} alt="search icon" className="absolute left-0 w-8 h-5 pl-3 top-4" />
        </div>

        {/* Table */}
        <div className="overflow-auto pt-14 h-full">
          <Table>
            <thead className="h-10">
              <tr>
                <th>Réalisé le</th>
                <th className="pl-10 xl:pl-20">Fait par</th>
                <th className="pl-10 xl:pl-20">Numéro</th>
                <th className="pl-10 xl:pl-20">Nom du client</th>
                <th className="pl-10 xl:pl-20">Nom du chantier</th>
                <th className="pl-10 xl:pl-20">Déplacer vers</th>
              </tr>
            </thead>
            <tbody>
              {sortedCoupons.length ? (
                sortedCoupons.map((coupon) => (
                  <tr
                    key={coupon.id}
                    className="h-16 border-b border-white-admin hover:bg-blue-50 transition-all cursor-pointer"
                    onClick={() => navigate(`/pdf-preview/intervention/${coupon.id}`)}
                  >
                    <td>{formatDate(coupon.createdAt)}</td>
                    <td className="pl-10 xl:pl-20">{coupon.username}</td>
                    <td className="pl-10 xl:pl-20">{coupon.numero}</td>
                    <td className="pl-10 xl:pl-20">{coupon.nameClient}</td>
                    <td className="pl-10 xl:pl-20">{coupon.nameWorksite}</td>
                    <td
                      className="pl-10 xl:pl-20"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <button
                        className="text-blue-admin border py-2 px-3 rounded-lg hover:bg-blue-100 transition"
                        onClick={() => handleMove(coupon.id)}
                      >
                        Déplacer
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-lg font-semibold pt-20">
                    {textSearch
                      ? "Oups, ce chantier ou client n'existe pas 🤭"
                      : "Ajouter un bon d'intervention"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Bouton "Charger plus" */}
          {nextToken && (
            <div className="flex justify-center my-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loadingMore ? 'Chargement…' : 'Charger plus'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal isVisible={showModalMove} styleModal="max-w-fit">
        <div className="p-5">
          <img src={AttentionIcon} alt="attention icon" />
          <p className="pt-7">Déplacer le bon de commande</p>

          <div className="flex space-x-6 py-5">
            <select
              onChange={handleSelectCompany}
              className="cursor-pointer w-[200px] text-blue-admin text-sm bg-blue-dropdown py-3 pl-5 pr-10 rounded-lg inputFocus"
              defaultValue=""
            >
              <option value="" disabled>
                Client
              </option>
              {namesCompagny.map((company) => (
                <option key={company.id} value={JSON.stringify(company)}>
                  {company.name}
                </option>
              ))}
            </select>

            <select
              onChange={handleSelectWorksite}
              value={selectWorksite}
              className="cursor-pointer w-[200px] text-blue-admin text-sm bg-blue-dropdown py-3 pl-5 pr-10 rounded-lg inputFocus"
            >
              <option value="" disabled>
                Chantier
              </option>
              {valuesWorksites.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </select>
          </div>

          <p className="text-gray-admin">
            Êtes-vous sûr de vouloir déplacer ce bon ? Cette action est définitive.
          </p>

          <div className="text-center pt-7">
            <button
              className="px-12 py-3 mr-4 font-semibold border rounded-xl text-red-error hover:bg-gray-100 transition"
              onClick={() => setShowModalMove(false)}
            >
              Annuler
            </button>
            <button
              disabled={!selectWorksite}
              onClick={confirmMoveCoupon}
              className={`px-10 py-3 text-white rounded-xl ${
                selectWorksite ? 'bg-red-error hover:bg-red-600' : 'bg-gray-placeholder cursor-not-allowed'
              }`}
            >
              Valider
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ListClassifyInterventionCoupon;
